import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Card,
  CardContent,
  Grid,
  CardActions,
  Button,
  Divider,
  CardHeader,
  IconButton,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import InputV2 from "./InputV2";
import MalhaSelectV2 from "./MalhaSelectV2";
import GerenciaSelectV2 from "./GerenciaSelectV2";
import PoloSelectV2 from "./PoloSelectV2";
import SubestacaoSelectV2 from "./SubestacaoSelectV2";
import AlimentadorSelectV2 from "./AlimentadorSelectV2";
import NsirStatusSelectV2 from "./NsirStatusSelectV2";
import UsuarioSelectV2 from "./UsuarioSelectV2";
import DatePickerV2 from "./DatePickerV2";
import TrueFalseSelectV2 from "./TrueFalseSelectV2";
import MultiSelectV2 from "./MultiSelectV2";
import DateTypeSelectV2 from "./DateTypeSelectV2";
import DefaultSelectV2 from "./DefaultSelectV2";
import OptionSelectV2 from "./OptionSelectV2";
import NsirSelectV2 from "./NsirSelectV2";
import NsirFilterFacetSelectV2 from "./NsirFilterFacetSelectV2";
import FilterFacetSelectV2 from "./FilterFacetSelectV2";
import EquipamentoSelectV2 from "./EquipamentoSelectV2";
import SearchSelectV2 from "./SearchSelectV2";
import ValidateSelectV2 from "./ValidateSelectV2";
import CartaoSelectV2 from "./CartaoSelectV2";
import VeiculoSelectV2 from "./VeiculoSelectV2";
import TipoEquipamentoSelectV2 from "./TipoEquipamentoSelectV2";
import NumberInputV2 from "./NumberInputV2";
import MultiSelectOptionsV2 from "./MultiSelectOptionsV2";
import MunicipioSelectV2 from "./MunicipioSelectV2";

import * as selectorsAuth from "../../store/auth/reducer";
import * as selectors from "../../store/filtro/reducer";
import * as actions from "../../store/filtro/actions";
import * as selectorsProduto from "../../store/produto/reducer";
import { loadProdutosOptions } from "../../store/produto/actions";
import { loadPedidoFornecedorOptions } from "../../store/pedido/actions";
import * as selectorsPedido from "../../store/pedido/reducer";
import * as Role from "../../constants/role";
import moment from "moment";
import { tipoServicoTemSubtipo } from "../../utils";
import { fetchEquipamentoModelos, filterModeloOptionsByTipo } from "../../helpers/equipamentoModelos";
import {
  fetchEquipamentoDistinctOptions,
  readTipoEquipamentos,
} from "../../helpers/equipamentoFilterOptions";

/** Cópia rasa do filtro — descarta chaves extras ao limpar. */
const cloneFilter = (filter) => {
  if (!filter || typeof filter !== "object") {
    return {};
  }
  return { ...filter };
};

const mapFornecedorOptions = (items) =>
  (items || []).map(({ value }) => ({ value, label: value }));

const EquipamentoModelosFilterField = ({ filter, setFilter, md = 4, xs = 12, limitTags = 2 }) => {
  const [allOptions, setAllOptions] = useState([]);
  const tiposKey = readTipoEquipamentos(filter).join(',');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchEquipamentoModelos();
        if (!cancelled) setAllOptions(next);
      } catch (e) {
        if (!cancelled) setAllOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(
    () => filterModeloOptionsByTipo(allOptions, tiposKey ? tiposKey.split(',') : []),
    [allOptions, tiposKey]
  );

  const showGroups = !tiposKey && options.some((option) => option.group);

  return (
    <Grid size={{ md, xs }}>
      <FilterFacetSelectV2
        mode="sync"
        label="Modelo"
        options={options}
        groupBy={showGroups ? ((option) => option.group || 'Outros') : undefined}
        limitTags={limitTags}
        value={filter.modelos}
        onSelect={(values) =>
          setFilter({
            ...filter,
            modelos: values || [],
          })
        }
      />
    </Grid>
  );
};

const EquipamentoDistinctFilterField = ({
  filter,
  setFilter,
  field,
  label,
  filterKey,
  legacyKey,
  md = 2,
  xs = 6,
  limitTags = 2,
}) => {
  const tiposKey = readTipoEquipamentos(filter).join(',');
  const loadAll = useCallback(
    () => fetchEquipamentoDistinctOptions(field, tiposKey ? tiposKey.split(',') : []),
    [field, tiposKey]
  );
  const listed = Array.isArray(filter[filterKey]) && filter[filterKey].length
    ? filter[filterKey]
    : null;
  const legacy = filter[legacyKey] || (legacyKey === 'codigoSisant' ? filter.sisant : null);
  const value = listed || (legacy ? [String(legacy)] : null);

  return (
    <Grid size={{ md, xs }}>
      <FilterFacetSelectV2
        mode="sync"
        label={label}
        value={value}
        reloadKey={tiposKey}
        limitTags={limitTags}
        loadAll={loadAll}
        onSelect={(values) => setFilter({
          ...filter,
          [filterKey]: values,
          [legacyKey]: null,
          ...(legacyKey === 'codigoSisant' ? { sisant: null } : {}),
        })}
      />
    </Grid>
  );
};

/** Domínio NSMP — multi estático no filtro NSIR. */
const PRIORIDADE_NSIR_OPTIONS = [
  { value: 'E', label: 'E' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

const hasFilterArrayValue = (v) => Array.isArray(v) && v.length > 0;

/** Multi-select geo: aceita scalar legado do cache e devolve array. */
const toFilterIdArray = (v) => {
  if (v == null || v === '') return [];
  return Array.isArray(v) ? v : [v];
};

const renderNsirFilterFacet = ({
  filter,
  setFilter,
  facet,
  label,
  filterKey,
  legacyKey,
  md = 2,
  xs = 6,
  minChars = 2,
  like = true,
}) => {
  const selectedIds = hasFilterArrayValue(filter[filterKey]) ? filter[filterKey] : null;
  const scalar = legacyKey != null ? filter[legacyKey] : null;
  const enableLike = Boolean(like) && Boolean(legacyKey);
  const likeValue = enableLike && !selectedIds && scalar != null && scalar !== ''
    ? String(scalar)
    : undefined;

  return (
    <Grid size={{ md, xs }}>
      <NsirFilterFacetSelectV2
        facet={facet}
        label={label}
        minChars={minChars}
        value={selectedIds}
        like={enableLike}
        likeValue={likeValue}
        scopeParams={{
          tipoServicos: filter.tipoServicos,
          subTipoIds: filter.subTipoIds,
        }}
        onSelect={(values, likeQuery) => {
          const next = { ...filter, [filterKey]: values };
          if (legacyKey) next[legacyKey] = enableLike ? (likeQuery || null) : null;
          setFilter(next);
        }}
      />
    </Grid>
  );
};

const fieldRenderers = {
  useDates: ({ filter, setFilter, md, xs = 6, useDateTypes, useDateTypesReadOnly, disabled = false }) => {
    // Com useDateTypes: md=2+2+8=12. Sem: md=6 cada (dialog/card legíveis para DD/MM/YYYY).
    const dateMd = md ?? (useDateTypes ? 2 : 6);
    const datesDisabled = typeof disabled === 'function' ? disabled(filter) : Boolean(disabled);
    return (
      <>
        <Grid size={{ md: dateMd, xs }}>
          <DatePickerV2
            label="Data Início"
            disabled={datesDisabled}
            onChange={(ts) => setFilter({ ...filter, inicio: ts })}
            value={filter.inicio}
          />
        </Grid>
        <Grid size={{ md: dateMd, xs }}>
          <DatePickerV2
            label="Data Fim"
            disabled={datesDisabled}
            onChange={(ts) => setFilter({ ...filter, fim: ts })}
            value={filter.fim}
          />
        </Grid>
        {useDateTypes ? (
          <Grid size={{ md: 8, xs: 12 }}>
            <DateTypeSelectV2
              label="Filtro de datas"
              onSelect={(dateTypes) => setFilter({ ...filter, dateTypes })}
              value={filter.dateTypes}
              disabled={useDateTypesReadOnly}
            />
          </Grid>
        ) : dateMd * 2 < 12 ? (
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: {
                xs: "none",
                md: "block",
              },
            }}
          />
        ) : null}
      </>
    );
  },

  useCodigo: ({ filter, setFilter, codigoLabel, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={codigoLabel || "Código"}
        onChange={(e) => setFilter({ ...filter, codigo: e.target.value })}
        value={filter.codigo}
      />
    </Grid>
  ),

  useFilterInput: ({ filter, setFilter, filterKey, label, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={label}
        onChange={(e) => setFilter({ ...filter, [filterKey]: e.target.value })}
        value={filter[filterKey] || ""}
      />
    </Grid>
  ),

  useEquipamento: (props) => renderNsirFilterFacet({
    ...props,
    facet: 'EQUIPAMENTO',
    label: props.label || 'Equipamento',
    filterKey: 'equipamentos',
    legacyKey: 'equipamento',
    minChars: props.minChars ?? 3,
  }),

  useNsirFilterFacet: (props) => renderNsirFilterFacet(props),

  useUsuarios: ({
    filter,
    setFilter,
    multiSelect,
    label = 'Usuários',
    grupos,
    disabled,
    md = 2,
    xs = 6,
    key: filterFieldKey = 'usuarioId',
    regimeTrabalhoIds,
    ativo,
  }) => (
    <Grid size={{ md, xs }}>
      <UsuarioSelectV2
        label={label}
        onSelect={(val) =>
          setFilter({
            ...filter,
            [filterFieldKey]: val,
          })}
        multiSelect={multiSelect}
        value={filter[filterFieldKey]}
        grupos={grupos}
        disabled={disabled}
        regimeTrabalhoIds={regimeTrabalhoIds}
        ativo={ativo}
      />
    </Grid>
  ),

  useRegimeTrabalho: ({
    filter,
    setFilter,
    md = 6,
    xs = 12,
    key = 'regimeTrabalhoIds',
    label = 'Regime de Trabalho',
    clearUsuarioKey = 'usuarioId',
  }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label={label}
        codigo="USUARIO_REGIME_TRABALHO"
        multiSelect
        onSelect={(values) =>
          setFilter({
            ...filter,
            [key]: values,
            ...(clearUsuarioKey ? { [clearUsuarioKey]: [] } : {}),
          })}
        value={filter[key]}
      />
    </Grid>
  ),

  useInspetor: ({
    filter,
    setFilter,
    multiSelect,
    userRole,
    md = 2,
    xs = 6,
    disabled = false,
    authUserId,
  }) => {
    const isSelfLocked = Role.INSPETOR === userRole;
    const value = isSelfLocked
      ? (hasFilterArrayValue(filter.inspetorId)
        ? filter.inspetorId
        : (authUserId ? [authUserId] : filter.inspetorId))
      : filter.inspetorId;

    return (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label="Inspetor"
          onSelect={(inspetorId) => {
            if (isSelfLocked) return;
            setFilter({ ...filter, inspetorId });
          }}
          multiSelect={multiSelect}
          grupos={[Role.ADMIN, Role.INSPETOR]}
          value={value}
          disabled={disabled || isSelfLocked}
          clearable={!isSelfLocked}
        />
      </Grid>
    );
  },

  useInspetorAuxiliar: ({ filter, setFilter, multiSelect, userRole, md = 2, xs = 6 }) =>
    Role.INSPETOR !== userRole && (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label="Inspetor Auxiliar"
          onSelect={(inspetorAuxiliarId) =>
            setFilter({ ...filter, inspetorAuxiliarId })
          }
          multiSelect={multiSelect}
          grupos={[Role.ADMIN, Role.INSPETOR]}
          value={filter.inspetorAuxiliarId}
        />
      </Grid>
    ),

  useDigitador: ({ filter, setFilter, disabled = false, md = 12, xs = 12, multiSelect }) => {
    return (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label="Digitador(es)"
          onSelect={digitadorId => setFilter({ ...filter, digitadorId })}
          grupos={[Role.ADMIN, Role.DIGITADOR]}
          value={filter.digitadorId}
          disabled={disabled}
          multiSelect={multiSelect}
        />
      </Grid>
    )
  },

  useMalha: ({ filter, setFilter, md = 3, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <MalhaSelectV2
        onSelect={(value) =>
          setFilter({
            ...filter,
            malhaId: value,
            gerenciaId: null,
            poloId: null,
            subestacaoId: null,
            alimentadorId: null,
          })
        }
        value={multiSelect ? toFilterIdArray(filter.malhaId) : filter.malhaId}
        multiSelect={multiSelect}
      />
    </Grid>
  ),

  useMunicipio: ({
    filter,
    setFilter,
    md = 12,
    xs = 12,
    multiSelect = true,
    filterKey = 'municipioIbge',
    key = 'codigoIbge',
    nomeKey = 'municipioNome',
    label = 'Cidades',
  }) => {
    const valueKey = multiSelect ? filterKey : key;

    return (
      <Grid size={{ md, xs }}>
        <MunicipioSelectV2
          label={label}
          multiSelect={multiSelect}
          value={filter[valueKey]}
          nome={multiSelect ? undefined : filter[nomeKey]}
          uf={multiSelect ? undefined : filter.uf}
          onSelect={(selected) => {
            if (multiSelect) {
              setFilter({ ...filter, [filterKey]: selected });
              return;
            }

            setFilter({
              ...filter,
              [key]: selected ? selected.codigoIbge : null,
              [nomeKey]: selected ? selected.nomeOriginal : null,
            });
          }}
        />
      </Grid>
    );
  },

  useGerencia: ({ filter, setFilter, md = 2, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <GerenciaSelectV2
        onSelect={(value) =>
          setFilter({
            ...filter,
            gerenciaId: value,
            poloId: null,
            subestacaoId: null,
            alimentadorId: null,
          })
        }
        value={multiSelect ? toFilterIdArray(filter.gerenciaId) : filter.gerenciaId}
        malhaId={multiSelect ? toFilterIdArray(filter.malhaId) : filter.malhaId}
        multiSelect={multiSelect}
      />
    </Grid>
  ),

  usePolo: ({ filter, setFilter, md = 3, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <PoloSelectV2
        onSelect={(value) =>
          setFilter({
            ...filter,
            poloId: value,
            subestacaoId: null,
            alimentadorId: null,
          })
        }
        value={multiSelect ? toFilterIdArray(filter.poloId) : filter.poloId}
        malhaId={multiSelect ? toFilterIdArray(filter.malhaId) : filter.malhaId}
        gerenciaId={multiSelect ? toFilterIdArray(filter.gerenciaId) : filter.gerenciaId}
        isMultiple={multiSelect}
      />
    </Grid>
  ),

  useSubestacao: ({ filter, setFilter, md = 2, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <SubestacaoSelectV2
        onSelect={(value) =>
          setFilter({ ...filter, subestacaoId: value, alimentadorId: null })
        }
        value={multiSelect ? toFilterIdArray(filter.subestacaoId) : filter.subestacaoId}
        malhaId={multiSelect ? toFilterIdArray(filter.malhaId) : filter.malhaId}
        gerenciaId={multiSelect ? toFilterIdArray(filter.gerenciaId) : filter.gerenciaId}
        poloId={multiSelect ? toFilterIdArray(filter.poloId) : filter.poloId}
        isMultiple={multiSelect}
      />
    </Grid>
  ),

  useAlimentador: ({ filter, setFilter, md = 2, xs = 6, multiSelect }) => (
    <Grid size={{ md, xs }}>
      <AlimentadorSelectV2
        onSelect={(value) => setFilter({ ...filter, alimentadorId: value })}
        value={multiSelect ? toFilterIdArray(filter.alimentadorId) : filter.alimentadorId}
        malhaId={multiSelect ? toFilterIdArray(filter.malhaId) : filter.malhaId}
        gerenciaId={multiSelect ? toFilterIdArray(filter.gerenciaId) : filter.gerenciaId}
        poloId={multiSelect ? toFilterIdArray(filter.poloId) : filter.poloId}
        subestacaoId={multiSelect ? toFilterIdArray(filter.subestacaoId) : filter.subestacaoId}
        multiSelect={multiSelect}
      />
    </Grid>
  ),

  usePossuiCroqui: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Croqui"
        onChange={(e) => setFilter({ ...filter, possuiCroqui: e.target.value })}
        value={filter.possuiCroqui}
      />
    </Grid>
  ),

  useNsirPagaInspetor: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Nsir Paga Inspetor"
        onChange={(e) =>
          setFilter({ ...filter, nsirPagaInspetor: e.target.value })
        }
        value={filter.nsirPagaInspetor}
      />
    </Grid>
  ),

  useCliente: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Clientes"
        codigo="CLIENTE"
        multiSelect
        onSelect={(values) => setFilter({ ...filter, clientes: values })}
        value={filter.clientes}
      />
    </Grid>
  ),

  useNsirStatus: ({
    filter,
    setFilter,
    customStatus,
    md = 12,
    xs = 12,
    multiSelect = true,
    filterKey = 'statuses',
    disabled = false,
    limitTags = 6,
  }) => (
    <Grid size={{ md, xs }}>
      <NsirStatusSelectV2
        onSelect={(value) => setFilter({ ...filter, [filterKey]: value })}
        value={filter[filterKey]}
        isMultiple={multiSelect}
        options={customStatus}
        disabled={disabled}
        limitTags={multiSelect ? limitTags : undefined}
      />
    </Grid>
  ),

  useTipoServico: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    multiSelect = true,
    label = 'Tipo de Serviços',
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label={label}
        codigo="TIPO_SERVICO"
        multiSelect={multiSelect}
        onSelect={tipoServicos => setFilter({ ...filter, tipoServicos, subTipoIds: null })}
        value={filter.tipoServicos}
        disabled={disabled}
      />
    </Grid>
  ),

  useCtPlan: (props) => renderNsirFilterFacet({
    ...props,
    facet: 'CT_PLAN',
    label: 'CT. Plan',
    filterKey: 'ctPlans',
    legacyKey: 'ctPlan',
    minChars: 2,
  }),

  useCtExec: (props) => renderNsirFilterFacet({
    ...props,
    facet: 'CT_EXEC',
    label: 'CT. Exec',
    filterKey: 'ctExecs',
    legacyKey: 'ctExec',
    minChars: 2,
  }),

  useApro: (props) => renderNsirFilterFacet({
    ...props,
    facet: 'APRO',
    label: 'APRO',
    filterKey: 'apros',
    legacyKey: 'apro',
    minChars: 1,
  }),

  useHd: (props) => renderNsirFilterFacet({
    ...props,
    facet: 'HD',
    label: 'HD',
    filterKey: 'hds',
    legacyKey: 'hd',
    minChars: 1,
  }),

  usePossuiHd: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui HD"
        onChange={(e) => setFilter({ ...filter, possuiHd: e.target.value || null })}
        value={filter.possuiHd}
      />
    </Grid>
  ),

  usePossuiApro: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Apro"
        onChange={(e) => setFilter({ ...filter, possuiApro: e.target.value || null })}
        value={filter.possuiApro}
      />
    </Grid>
  ),

  useNumNsmp: (props) => renderNsirFilterFacet({
    ...props,
    facet: 'NUM_NSMP',
    label: 'Nº Nsmp',
    filterKey: 'numNsmps',
    legacyKey: 'numNsmp',
    minChars: 2,
  }),

  usePrioridade: ({ filter, setFilter, md = 2, xs = 6 }) => {
    const selected = hasFilterArrayValue(filter.prioridades)
      ? filter.prioridades
      : (filter.prioridade ? [filter.prioridade] : []);
    return (
      <Grid size={{ md, xs }}>
        <MultiSelectV2
          label="Prioridade"
          options={PRIORIDADE_NSIR_OPTIONS}
          defaultValue={PRIORIDADE_NSIR_OPTIONS.filter((o) => selected.indexOf(o.value) > -1)}
          onChange={(values) => setFilter({
            ...filter,
            prioridades: values && values.length ? values.map((v) => v.value) : null,
            prioridade: null,
          })}
        />
      </Grid>
    );
  },

  useAdere: ({
    filter,
    setFilter,
    adereMd = 2,
    adereMesesMd = 2,
    xs = 6,
  }) => (
    <>
      <Grid size={{ md: adereMd, xs }}>
        <TrueFalseSelectV2
          label="Adere"
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'false') {
              setFilter({ ...filter, possuiAdere: value, adereMonths: null });
            } else {
              setFilter({ ...filter, possuiAdere: value });
            }
          }}
          value={filter.possuiAdere}
        />
      </Grid>
      <Grid size={{ md: adereMesesMd, xs }}>
        <MultiSelectOptionsV2
          label="Adere Meses"
          url="nsirs/options/aderes"
          onSelect={(values) => setFilter({ ...filter, adereMonths: values })}
          value={filter.adereMonths}
          disabled={filter.possuiAdere === 'false'}
        />
      </Grid>
    </>
  ),

  useSubTipoServico: ({ filter, setFilter, md = 2, xs = 6, multiSelect = true }) => {
    const parentIds = (filter.tipoServicos || []).filter(tipoServicoTemSubtipo);
    const hasSubtipoTipo = parentIds.length > 0;

    return (
      <Grid size={{ md, xs }}>
        <OptionSelectV2
          label="Subtipo de Serviços"
          codigo="NSIR_SUBTIPOS"
          multiSelect={multiSelect}
          parentIds={hasSubtipoTipo ? parentIds : undefined}
          disabled={!hasSubtipoTipo}
          onSelect={subTipoIds => setFilter({ ...filter, subTipoIds })}
          value={filter.subTipoIds}
        />
      </Grid>
    );
  },

  useClientes: ({ filter, setFilter, md = 2, xs = 6, multiSelect = true }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Clientes"
        codigo="CLIENTE"
        multiSelect={multiSelect}
        onSelect={clienteIds => setFilter({ ...filter, clienteIds })}
        value={filter.clienteIds}
      />
    </Grid>
  ),

  useOption: ({ filter, setFilter, code, label, key, md = 2, xs = 6, multiSelect = true }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label={label}
        codigo={code}
        multiSelect={multiSelect}
        onSelect={(values) => setFilter({ ...filter, [key]: values })}
        value={filter[key]}
      />
    </Grid>
  ),

  useMultiSelectCustomOption: ({ filter, setFilter, options, label, key, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <MultiSelectV2
        label={label}
        onChange={values => setFilter({ ...filter, [key]: values ? values.map(v => v.value) : [] })}
        defaultValue={
          options && filter[key] && filter[key].length
            ? options.filter(o => filter[key].indexOf(o.value) > -1)
            : []
        }
        options={options}
      />
    </Grid>
  ),

  useMesReferencia: ({ filter, setFilter, md = 2, xs = 6, label = "Mês Referência", key = "mesReferencia" }) => (
    <Grid size={{ md, xs }}>
      <DatePickerV2
        label={label}
        yearMonth
        showInput
        onChange={(data) => setFilter({ ...filter, [key]: data ? moment(data).format('YYYY-MM') : null })}
        value={filter[key] ? moment(filter[key], 'YYYY-MM').valueOf() : null}
      />
    </Grid>
  ),

  useNsirs: ({
    filter,
    setFilter,
    label = 'Nsirs',
    key = 'nsirIds',
    md = 2,
    xs = 6,
    minChars = 2,
    like = false,
  }) => {
    const selectedIds = hasFilterArrayValue(filter[key]) ? filter[key] : null;
    const likeValue = like && !selectedIds && filter.codigo
      ? String(filter.codigo)
      : undefined;
    return (
      <Grid size={{ md, xs }}>
        <NsirSelectV2
          label={label}
          minChars={minChars}
          like={like}
          likeNoun="Código"
          likeValue={likeValue}
          tipoServicos={filter.tipoServicos}
          subTipoIds={filter.subTipoIds}
          onSelect={(values, _labels, likeQuery) => setFilter({
            ...filter,
            [key]: values,
            codigo: like ? (likeQuery || null) : null,
          })}
          value={selectedIds}
        />
      </Grid>
    );
  },

  useSelectOption: ({ filter, setFilter, md = 2, xs = 6, options, key, label, showEmpty = true, apply }) => (
    <Grid size={{ md, xs }}>
      <DefaultSelectV2
        label={label}
        name={key}
        showEmpty={showEmpty}
        options={options}
        onSelect={(e) => {
          const value = e.target.value || null;
          const next = { ...filter, [key]: value };
          setFilter(apply ? apply(next) : next);
        }}
        value={filter[key]}
      />
    </Grid>
  ),

  useSearchSelect: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    options,
    key,
    label,
    multiSelect = false,
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label={label}
        multiSelect={multiSelect}
        options={options || []}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
        disabled={disabled}
      />
    </Grid>
  ),

  useVeiculo: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    key = "veiculoIds",
    label = "Veículos",
    multiSelect = true,
    disabled = false,
    ativo = true,
  }) => (
    <Grid size={{ md, xs }}>
      <VeiculoSelectV2
        label={label}
        multiSelect={multiSelect}
        disabled={disabled}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
        ativo={ativo}
      />
    </Grid>
  ),

  useCategoriaVeiculo: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label="Categorias"
        multiSelect
        onSelect={(categoriaVeiculos) => setFilter({ ...filter, categoriaVeiculos })}
        options={[
          { value: 'BICICLETA_ELETRICA', label: 'Bicicleta Elétrica' },
          { value: 'CARRO', label: 'Carro' },
          { value: 'CAMINHAO', label: 'Caminhão' },
          { value: 'UTILITARIO_CAMINHONETE', label: 'Caminhonete' },
          { value: 'MAQUINA', label: 'Máquina' },
          { value: 'MOTO', label: 'Moto' },
          { value: 'REBOQUE', label: 'Reboque' },
        ]}
        value={filter.categoriaVeiculos}
      />
    </Grid>
  ),

  useTipoVeiculo: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label="Proprietários"
        multiSelect
        onSelect={(tipoVeiculos) => setFilter({ ...filter, tipoVeiculos })}
        options={[
          { value: 'ELETROPONTES', label: 'Eletropontes' },
          { value: 'PARTICULAR', label: 'Particular' },
          { value: 'ALUGADO', label: 'Alugado' },
        ]}
        value={filter.tipoVeiculos}
      />
    </Grid>
  ),

  useFinalidadeVeiculo: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Finalidades"
        codigo="VEICULO_FINALIDADES"
        multiSelect
        onSelect={(finalidades) => setFilter({ ...filter, finalidades })}
        value={filter.finalidades}
      />
    </Grid>
  ),

  useAtivo: ({ filter, setFilter, md = 2, xs = 6, useAtivoLabel }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label={useAtivoLabel || 'Ativo'}
        onChange={(e) => setFilter({ ...filter, ativo: e.target.value || null })}
        value={filter.ativo}
      />
    </Grid>
  ),

  useRole: ({ filter, setFilter, md = 3, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <DefaultSelectV2
        label="Função"
        showEmpty
        options={[
          { id: 'ROLE_ADMIN', nome: 'Administrador' },
          { id: 'ROLE_CROQUI', nome: 'Croqui' },
          { id: 'ROLE_DIGITADOR', nome: 'Digitador' },
          { id: 'ROLE_INSPETOR', nome: 'Inspetor' },
          { id: 'ROLE_AUDITORIA_NSIR', nome: 'Auditoria NSIR' },
        ]}
        onSelect={(e) => setFilter({ ...filter, role: e.target.value || null })}
        value={filter.role}
      />
    </Grid>
  ),

  useSetor: ({ filter, setFilter, md = 3, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <OptionSelectV2
        label="Setor"
        codigo="USUARIO_SETOR"
        onSelect={(setorId) => setFilter({ ...filter, setorId })}
        value={filter.setorId}
      />
    </Grid>
  ),

  usePossuiCarteira: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Carteira"
        onChange={(e) => setFilter({ ...filter, possuiCarteira: e.target.value || null })}
        value={filter.possuiCarteira}
      />
    </Grid>
  ),

  useTipoUsuarioDocumento: ({ filter, setFilter, md = 4, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <DefaultSelectV2
        label="Tipo"
        showEmpty
        options={[
          { id: 'DOCUMENTO', nome: 'Documento' },
          { id: 'CERTIFICADO', nome: 'Certificado' },
        ]}
        onSelect={(e) => setFilter({ ...filter, tipo: e.target.value || null })}
        value={filter.tipo}
      />
    </Grid>
  ),

  usePossuiValidade: ({ filter, setFilter, md = 3, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Validade"
        onChange={(e) => setFilter({ ...filter, possuiValidade: e.target.value || null })}
        value={filter.possuiValidade}
      />
    </Grid>
  ),

  useValidade: ({ filter, setFilter, md = 3, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <NumberInputV2
        label="Validade (em anos)"
        onChange={(valor) => setFilter({ ...filter, validadeEmAnos: valor })}
        value={filter.validadeEmAnos}
        decimalScale={0}
        prefix=""
        thousandSeparator=""
      />
    </Grid>
  ),

  useGaragem: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Está na Garagem"
        onChange={(e) => setFilter({ ...filter, garagem: e.target.value || null })}
        value={filter.garagem}
      />
    </Grid>
  ),

  usePosseEletropontes: ({ filter, setFilter, md = 4, xs = 12 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Está com Eletropontes"
        onChange={(e) => setFilter({ ...filter, posseEletropontes: e.target.value || null })}
        value={filter.posseEletropontes}
      />
    </Grid>
  ),

  useColaborador: ({ filter, setFilter, md = 4, xs = 12, multiSelect = false, limitTags = 2 }) => {
    const colaboradores = Array.isArray(filter.colaboradores) && filter.colaboradores.length
      ? filter.colaboradores
      : (filter.colaborador ? [filter.colaborador] : []);
    return (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label="Colaborador"
          multiSelect={multiSelect}
          limitTags={multiSelect ? limitTags : undefined}
          ativo
          value={multiSelect ? colaboradores : filter.colaborador}
          onSelect={(colaborador) => {
            if (!multiSelect) {
              setFilter({ ...filter, colaborador });
              return;
            }
            const ids = colaborador && colaborador.length ? colaborador : null;
            setFilter({
              ...filter,
              colaboradores: ids,
              colaborador: ids && ids.length === 1 ? ids[0] : null,
            });
          }}
        />
      </Grid>
    );
  },

  usePossuiRastreador: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Rastreador"
        onChange={(e) => setFilter({ ...filter, possuiRastreador: e.target.value || null })}
        value={filter.possuiRastreador}
      />
    </Grid>
  ),

  useNumRastreador: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. Rastreador"
        onChange={(e) => setFilter({ ...filter, numRastreador: e.target.value })}
        value={filter.numRastreador || ''}
      />
    </Grid>
  ),

  usePossuiCamera: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label="Possui Camera"
        onChange={(e) => setFilter({ ...filter, possuiCamera: e.target.value || null })}
        value={filter.possuiCamera}
      />
    </Grid>
  ),

  useNumCamera: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. de Camera"
        onChange={(e) => setFilter({ ...filter, numCamera: e.target.value })}
        value={filter.numCamera || ''}
      />
    </Grid>
  ),

  useNumFrota: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label="Num. de Frota"
        onChange={(e) => setFilter({ ...filter, numFrota: e.target.value })}
        value={filter.numFrota || ''}
      />
    </Grid>
  ),

  useTipoEquipamento: ({ filter, setFilter, md = 3, xs = 12, limitTags = 2 }) => (
    <Grid size={{ md, xs }}>
      <TipoEquipamentoSelectV2
        multiSelect
        limitTags={limitTags}
        value={readTipoEquipamentos(filter)}
        onChange={(tipoEquipamentos) => {
          const tipos = tipoEquipamentos && tipoEquipamentos.length ? tipoEquipamentos : null;
          setFilter({
            ...filter,
            tipoEquipamentos: tipos,
            tipoEquipamento: tipos && tipos.length === 1 ? tipos[0] : null,
            modelos: [],
          });
        }}
      />
    </Grid>
  ),

  useEquipamentoModelos: (props) => <EquipamentoModelosFilterField {...props} />,

  useNumPatrimonio: (props) => (
    <EquipamentoDistinctFilterField
      {...props}
      field="numPatrimonio"
      label="Num. Patrimônio"
      filterKey="numPatrimonios"
      legacyKey="numPatrimonio"
    />
  ),

  useNumSerie: (props) => (
    <EquipamentoDistinctFilterField
      {...props}
      field="numSerie"
      label="Num. de Série"
      filterKey="numSeries"
      legacyKey="numSerie"
    />
  ),

  useSisant: (props) => (
    <EquipamentoDistinctFilterField
      {...props}
      field="codigoSisant"
      label="Código SISANT"
      filterKey="codigoSisants"
      legacyKey="codigoSisant"
    />
  ),

  useInspetorIds: ({ filter, setFilter, userRole, label = "Inspetor", md = 2, xs = 6 }) =>
    Role.INSPETOR !== userRole && (
      <Grid size={{ md, xs }}>
        <UsuarioSelectV2
          label={label}
          onSelect={(inspetorIds) => setFilter({ ...filter, inspetorIds })}
          multiSelect
          grupos={[Role.ADMIN, Role.INSPETOR]}
          value={filter.inspetorIds}
        />
      </Grid>
    ),

  useEquipamentoOptions: ({
    filter,
    setFilter,
    label = "Equipamentos",
    tipoEquipamento = "DRONE",
    filterUsuarioUltimoRepasse = false,
    showUsuarioTodosRepasse = false,
    md = 2,
    xs = 6,
  }) => (
    <Grid size={{ md, xs }}>
      <EquipamentoSelectV2
        label={label}
        multiSelect
        tipoEquipamento={tipoEquipamento}
        filterUsuarioUltimoRepasse={filterUsuarioUltimoRepasse}
        showUsuarioTodosRepasse={showUsuarioTodosRepasse}
        value={filter.droneIds}
        onSelect={(droneIds) => setFilter({ ...filter, droneIds })}
      />
    </Grid>
  ),

  useCidade: ({ filter, setFilter, md = 2, xs = 6, label = "Cidade" }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={label}
        onChange={(e) => setFilter({ ...filter, cidade: e.target.value })}
        value={filter.cidade || ""}
      />
    </Grid>
  ),

  useValidate: ({ filter, setFilter, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <ValidateSelectV2
        showEmpty
        onChange={(validate) =>
          setFilter({
            ...filter,
            validate,
          })
        }
        value={filter.validate}
      />
    </Grid>
  ),

  useSimNao: ({ filter, setFilter, md = 2, xs = 6, key, label }) => (
    <Grid size={{ md, xs }}>
      <TrueFalseSelectV2
        label={label}
        name={key}
        onChange={(e) => setFilter({ ...filter, [key]: e.target.value || null })}
        value={filter[key]}
      />
    </Grid>
  ),

  useCartao: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    multiSelect = false,
    key: filterFieldKey = 'cartao',
    label,
    disabled = false,
  }) => (
    <Grid size={{ md, xs }}>
      <CartaoSelectV2
        label={label}
        multiSelect={multiSelect}
        disabled={disabled}
        onSelect={(value) =>
          setFilter({
            ...filter,
            [filterFieldKey]: value,
          })
        }
        value={filter[filterFieldKey]}
      />
    </Grid>
  ),

  useNome: ({ filter, setFilter, nomeLabel, md = 2, xs = 6 }) => (
    <Grid size={{ md, xs }}>
      <InputV2
        label={nomeLabel || "Nome"}
        onChange={(e) => setFilter({ ...filter, nome: e.target.value })}
        value={filter.nome}
      />
    </Grid>
  ),

  useFornecedor: ({
    filter,
    setFilter,
    fornecedorLabel,
    label,
    md = 2,
    xs = 6,
    options,
    key = "fornecedor",
    multiSelect = false,
  }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label={label || fornecedorLabel || "Fornecedor"}
        multiSelect={multiSelect}
        options={options || []}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
      />
    </Grid>
  ),

  useProduto: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    options,
    key = 'produto',
    label = 'Produto',
    multiSelect = false,
  }) => (
    <Grid size={{ md, xs }}>
      <SearchSelectV2
        label={label}
        multiSelect={multiSelect}
        options={options || []}
        onSelect={(value) => setFilter({ ...filter, [key]: value })}
        value={filter[key]}
      />
    </Grid>
  ),

  useLocal: ({
    filter,
    setFilter,
    md = 2,
    xs = 6,
    key = 'local',
    label = 'Local',
    options,
    multiSelect = false,
  }) => {
    const localOptions = (options || [
      { id: 'Almoxarifado I  C17', nome: 'Almoxarifado I  C17' },
      { id: 'Almoxarifado II  C17', nome: 'Almoxarifado II  C17' },
      { id: 'Sala Drones', nome: 'Sala Drones' },
      { id: 'Sucata', nome: 'Sucata' },
    ]).map((o) => ({ value: o.id, label: o.nome }));

    if (multiSelect) {
      return (
        <Grid size={{ md, xs }}>
          <SearchSelectV2
            label={label}
            multiSelect
            options={localOptions}
            onSelect={(value) => setFilter({ ...filter, [key]: value })}
            value={filter[key]}
          />
        </Grid>
      );
    }

    return (
      <Grid size={{ md, xs }}>
        <DefaultSelectV2
          label={label}
          name={key}
          showEmpty
          options={options || [
            { id: 'Almoxarifado I  C17', nome: 'Almoxarifado I  C17' },
            { id: 'Almoxarifado II  C17', nome: 'Almoxarifado II  C17' },
            { id: 'Sala Drones', nome: 'Sala Drones' },
            { id: 'Sucata', nome: 'Sucata' },
          ]}
          onSelect={(e) => setFilter({ ...filter, [key]: e.target.value || null })}
          value={filter[key]}
        />
      </Grid>
    );
  },

  useAno: ({ filter, setFilter, md = 2, xs = 6, key = "ano", label = "Ano" }) => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y -= 1) years.push(y);
    return (
      <Grid size={{ md, xs }}>
        <DefaultSelectV2
          label={label}
          name={key}
          showEmpty={false}
          options={years.map((y) => ({ id: String(y), nome: String(y) }))}
          onSelect={(e) => setFilter({ ...filter, [key]: e.target.value ? Number(e.target.value) : null })}
          value={filter[key] != null ? String(filter[key]) : String(currentYear)}
        />
      </Grid>
    );
  },

  usePedidoVooStatus: ({ filter, setFilter, md = 12, xs = 12, label = "Status" }) => {
    const options = [
      { value: "AGUARDANDO_VALIDACAO", label: "Aguardando Aprovação" },
      { value: "VALIDADO", label: "Aprovação" },
      { value: "INVALIDADO", label: "Negado" },
      { value: "EXPIRED", label: "Expirado" },
    ];
    return (
      <Grid size={{ md, xs }}>
        <MultiSelectV2
          label={label}
          onChange={(values) =>
            setFilter({
              ...filter,
              status: values ? values.map((v) => v.value) : [],
            })
          }
          defaultValue={
            filter.status && filter.status.length
              ? options.filter((o) => filter.status.indexOf(o.value) > -1)
              : []
          }
          options={options}
        />
      </Grid>
    );
  },
};

const renderFieldList = ({
  fields,
  filter,
  setFilter,
  userRole,
  authUserId,
  produtoSearchOptions,
  fornecedorSearchOptions,
}) =>
  (fields || []).map(({ field, fields: nestedFields, label, defaultExpanded, getActiveSummary, ...rest }, idx) => {
    if (field === 'useMoreFiltersCollapse') {
      return (
        <MoreFiltersCollapse
          key={`more-${idx}`}
          label={label || 'Mais filtros'}
          defaultExpanded={!!defaultExpanded}
          getActiveSummary={getActiveSummary}
          fields={nestedFields || []}
          filter={filter}
          setFilter={setFilter}
          userRole={userRole}
          authUserId={authUserId}
          produtoSearchOptions={produtoSearchOptions}
          fornecedorSearchOptions={fornecedorSearchOptions}
        />
      );
    }

    const Renderer = fieldRenderers[field];
    if (!Renderer) return null;

    const rendererProps = {
      filter,
      setFilter,
      userRole,
      authUserId,
      label,
      ...rest,
    };

    if (field === 'useProduto' && !rendererProps.options) {
      rendererProps.options = produtoSearchOptions;
    }

    if (field === 'useFornecedor' && !rendererProps.options) {
      rendererProps.options = fornecedorSearchOptions;
    }

    return (
      <React.Fragment key={`${field}-${idx}`}>
        {Renderer(rendererProps)}
      </React.Fragment>
    );
  });

const MoreFiltersCollapse = ({
  label,
  defaultExpanded,
  fields,
  filter,
  setFilter,
  userRole,
  authUserId,
  produtoSearchOptions,
  fornecedorSearchOptions,
  getActiveSummary,
}) => {
  const summaryItems = typeof getActiveSummary === 'function'
    ? getActiveSummary(filter)
    : [];
  const hasActive = Array.isArray(summaryItems) && summaryItems.length > 0;
  const [openMore, setOpenMore] = useState(Boolean(defaultExpanded) || hasActive);
  const [userCollapsed, setUserCollapsed] = useState(false);

  useEffect(() => {
    if (hasActive && !userCollapsed) {
      setOpenMore(true);
    }
    if (!hasActive) {
      setUserCollapsed(false);
    }
  }, [hasActive, userCollapsed]);

  const headerLabel = hasActive
    ? `${label} · ${summaryItems.length} ativo${summaryItems.length > 1 ? 's' : ''}`
    : label;

  return (
    <Grid size={{ xs: 12 }}>
      <Box
        sx={{
          border: 1,
          borderColor: hasActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          mt: 0.5,
        }}
      >
        <Button
          fullWidth
          color="primary"
          variant="outlined"
          onClick={() => {
            setOpenMore((v) => {
              const next = !v;
              if (!next && hasActive) setUserCollapsed(true);
              if (next) setUserCollapsed(false);
              return next;
            });
          }}
          endIcon={
            <ExpandMoreIcon
              sx={{
                transform: openMore ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: (theme) =>
                  theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shortest,
                  }),
              }}
            />
          }
          sx={{
            justifyContent: 'space-between',
            px: 1.5,
            py: 1.25,
            textTransform: 'none',
            borderRadius: 0,
            borderWidth: 0,
            borderBottom: openMore || (hasActive && !openMore) ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 600 }}>
            {openMore ? '▾ ' : '▸ '}{headerLabel}
          </Typography>
        </Button>
        {!openMore && hasActive ? (
          <Box sx={{ px: 1.5, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {summaryItems.map((item) => (
              <Box
                key={item}
                component="span"
                sx={{
                  fontSize: 12,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  bgcolor: 'action.selected',
                  color: 'text.secondary',
                }}
              >
                {item}
              </Box>
            ))}
          </Box>
        ) : null}
        <Collapse in={openMore} timeout="auto" unmountOnExit={false}>
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Grid container spacing={2}>
              {renderFieldList({
                fields,
                filter,
                setFilter,
                userRole,
                authUserId,
                produtoSearchOptions,
                fornecedorSearchOptions,
              })}
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </Grid>
  );
};

MoreFiltersCollapse.propTypes = {
  label: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  fields: PropTypes.array,
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  userRole: PropTypes.string,
  authUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  produtoSearchOptions: PropTypes.array,
  fornecedorSearchOptions: PropTypes.array,
  getActiveSummary: PropTypes.func,
};

const CustomFilterV2 = ({
  fields = [],
  onFilter,
  userRole,
  authUserId,
  produtoOptions,
  fornecedorOptions,
  loadProdutosOptions,
  loadPedidoFornecedorOptions,
  defaultFilter = {},
  appliedFilter,
  defaultCollapseValue = false,
  defaultCollapseUnmountOnExit = true,
  confirmButtonText = "Filtrar",
  variant = "card",
  open = false,
  onClose = null,
  title = "Filtros",
  maxWidth = "md",
  fullWidth = true,
}) => {
  const [filter, setFilter] = useState(() => cloneFilter(defaultFilter));
  const [filterFormKey, setFilterFormKey] = useState(0);
  const [expanded, setExpanded] = useState(defaultCollapseValue);
  const prevDialogOpen = useRef(false);

  const hasUseProduto = useMemo(
    () => fields.some(({ field }) => field === "useProduto"),
    [fields]
  );

  const hasUseFornecedor = useMemo(
    () => fields.some(({ field }) => field === "useFornecedor"),
    [fields]
  );

  const produtoSearchOptions = useMemo(
    () => (produtoOptions || []).map((p) => ({ value: p.id, label: p.nome })),
    [produtoOptions]
  );

  const fornecedorSearchOptions = useMemo(
    () => mapFornecedorOptions(fornecedorOptions),
    [fornecedorOptions]
  );

  useEffect(() => {
    if (hasUseProduto) {
      loadProdutosOptions();
    }
  }, [hasUseProduto, loadProdutosOptions]);

  useEffect(() => {
    if (hasUseFornecedor) {
      loadPedidoFornecedorOptions();
    }
  }, [hasUseFornecedor, loadPedidoFornecedorOptions]);

  useEffect(() => {
    if (variant === "dialog") {
      const justOpened = open && !prevDialogOpen.current;
      prevDialogOpen.current = open;
      if (!justOpened) {
        return;
      }
      const source =
        appliedFilter !== undefined && appliedFilter !== null
          ? appliedFilter
          : defaultFilter;
      setFilter(cloneFilter(source));
      return;
    }

    if (variant === "inline" && appliedFilter !== undefined && appliedFilter !== null) {
      setFilter(cloneFilter(appliedFilter));
    }
  }, [open, variant, appliedFilter, defaultFilter]);

  const renderedFields = useMemo(
    () =>
      renderFieldList({
        fields,
        filter,
        setFilter,
        userRole,
        authUserId,
        produtoSearchOptions,
        fornecedorSearchOptions,
      }),
    [fields, filter, userRole, authUserId, produtoSearchOptions, fornecedorSearchOptions]
  );

  const handleApplyFilter = () => {
    const currentFilter = { ...filter };
    if (!currentFilter.status) currentFilter.status = null;
    return onFilter(currentFilter);
  };

  const handleClearFilter = () => {
    setFilter(cloneFilter(defaultFilter));
    setFilterFormKey((key) => key + 1);
  };

  if (variant === "inline") {
    return (
      <Grid container spacing={1} key={filterFormKey}>
        {renderedFields}
      </Grid>
    );
  }

  if (variant === "dialog") {
    return (
      <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth={fullWidth}>
        <DialogTitle
          sx={{
            bgcolor: "background.paper",
            color: "text.primary",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          {title}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: "background.paper",
            borderColor: "divider",
            pt: 3,
          }}
        >
          <Grid container spacing={2} key={filterFormKey}>
            {renderedFields}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Button color="primary" size="small" variant="outlined" onClick={handleClearFilter}>
            Limpar
          </Button>
          <Button color="primary" size="small" variant="outlined" onClick={onClose}>
            Fechar
          </Button>
          <Button color="primary" size="small" variant="contained" onClick={handleApplyFilter}>
            {confirmButtonText}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        bgcolor: "background.paper",
        overflow: "inherit",
      }}
    >
      <CardHeader
        title={title}
        onClick={() => {
          if (defaultCollapseUnmountOnExit) setExpanded(!expanded);
        }}
        sx={{
          pt: 1.25,
          px: 2,
          pb: 0,
          cursor: "pointer",
        }}
        action={
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              if (defaultCollapseUnmountOnExit) setExpanded(!expanded);
            }}
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              ml: "auto",
              transition: (theme) =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit={defaultCollapseUnmountOnExit}>
        <CardContent>
          <Grid container spacing={2} key={filterFormKey}>
            {renderedFields}
          </Grid>
        </CardContent>
        <Divider sx={{ bgcolor: "divider" }} />
        <CardActions sx={{ p: 2 }}>
          <Button color="primary" size="small" variant="contained" onClick={handleApplyFilter}>
            {confirmButtonText}
          </Button>
          <Button color="primary" size="small" variant="contained" onClick={handleClearFilter}>
            Limpar
          </Button>
        </CardActions>
      </Collapse>
    </Card>
  );
};

CustomFilterV2.propTypes = {
  fields: PropTypes.array,
  onFilter: PropTypes.func.isRequired,
  userRole: PropTypes.string,
  authUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultFilter: PropTypes.object,
  /** Valores já aplicados na tela; ao abrir o dialog, o formulário sincroniza com este objeto. */
  appliedFilter: PropTypes.object,
  defaultCollapseValue: PropTypes.bool,
  defaultCollapseUnmountOnExit: PropTypes.bool,
  confirmButtonText: PropTypes.string,
  variant: PropTypes.oneOf(["card", "dialog", "inline"]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  fullWidth: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actions,
      loadProdutosOptions,
      loadPedidoFornecedorOptions,
    },
    dispatch
  );

const mapStateToProps = (state) => ({
  cacheFiltroNsir: selectors.getCacheFiltroNsir(state),
  userRole: selectorsAuth.getRole(state),
  authUserId: selectorsAuth.getUserId(state),
  produtoOptions: selectorsProduto.getProdutoOptions(state),
  fornecedorOptions: selectorsPedido.getPedidoFornecedorOptions(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomFilterV2);
